import {FormControl} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {always} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useRenderCustomerMessageEmailTemplateMutation} from '@dms/api/core';
import i18n from '@dms/i18n';

import {useDebouncedCallback, useDeepCompareEffect} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {FormValues} from '../types/SendCustomerEmailFormSchema';
import {EmailWindow} from './EmailWindow';

interface EmailPreviewProps {
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
}

export function EmailPreview(props: EmailPreviewProps) {
  const [renderEmail, {isLoading: isLoadingEmail}] =
    useRenderCustomerMessageEmailTemplateMutation();

  const [htmlData, setHtmlData] = useState<string | null>(null);

  const data = useWatch({
    control: props.control,
    name: ['customerId', 'customerEmailAddress', 'body', 'subject'],
  });

  const isRequiredDataMissing = match(data.map(isNilOrEmpty))
    .with(
      // when customerId is missing, we cannot fetch
      [true, Pattern.boolean, Pattern.boolean, Pattern.boolean],
      // when customerEmailAddress is missing, we cannot fetch
      [Pattern.boolean, true, Pattern.boolean, Pattern.boolean],
      // when subject is missing, we cannot fetch
      [Pattern.boolean, Pattern.boolean, true, true],
      always(true)
    )
    .otherwise(always(false));

  const handleRenderEmail = () => {
    const formValues = props.formApi.getValues();

    if (
      isNilOrEmpty(formValues.customerId) ||
      isNilOrEmpty(formValues.customerEmailAddress) ||
      (isNilOrEmpty(formValues.subject) && isNilOrEmpty(formValues.body))
    ) {
      setHtmlData(null);
      return;
    }

    renderEmail({
      customerMessageId: formValues.customerMessageId?.[0] ?? 'customer-message',
      renderCustomerMessageRequestBody: {
        customerMessageId: formValues.customerMessageId?.[0] ?? 'customer-message',
        customerId: formValues.customerId,
        payload: {
          body: formValues.body?.toString() ?? '',
          subject: formValues.subject?.toString() ?? '',
        },
      },
    })
      .unwrap()
      .then((data) => setHtmlData(data.body))
      .catch(handleApiError);
  };

  const debouncedHandleRenderEmail = useDebouncedCallback(handleRenderEmail, 1000);

  useDeepCompareEffect(() => {
    if (isRequiredDataMissing) {
      setHtmlData(null);
      return;
    }

    debouncedHandleRenderEmail();
  }, [data]);

  return (
    <VStack height="100%">
      <Box position="relative" height="100%" minHeight={[75, 100]}>
        <Show when={isNilOrEmpty(htmlData)}>
          <EmailPreviewButtonWrapper data-testid="customerEmailForm-email-window-empty-preview">
            <VStack align="center">
              <Text size="xSmall" align="center" color="tertiary">
                {i18n.t('entity.customer.labels.fillAllRequiredFields')}
              </Text>
            </VStack>
          </EmailPreviewButtonWrapper>
        </Show>
        <EmailWindow
          htmlData={htmlData}
          isRerendering={isLoadingEmail && isNotNilOrEmpty(htmlData)}
          data-testid="customerEmailForm-email-window"
        />
      </Box>
    </VStack>
  );
}

const EmailPreviewButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
